import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import { Box, Typography, Chip, CardCredit } from '@dxb-web-ui-framework/design-system';
import { partiallyMaskValue } from '@dxb-web-ui-framework/common/utils/DataObfuscation';
import React from 'react';
import { ActionAuthorizationButtons } from '../../../../../../authorizations/src';
import { ViewDetailsButton } from '../../ViewDetailsButton';
import { PayrollRecordStatusChip } from '../../../../../../payroll/src/components/PayrollSteper/PayrollConfirm/PayrollRecordStatusChip';
import { TransactionOrderStatusChip } from '../../../../../../transaction/src/components';
import { Card as CardType } from '../../../../../../card/src/domain/Card/entities/Card';
import { CardStatusChip } from '../../../../../../card/src/components/CardStatusChips';
import { PayrollStatusChip } from '../../../../../../payroll/src/components';
import { PayrollDetailsStatusChip } from '../../../../../../payroll/src/components/PayrollDetailsStatusChip';
import { CardSize } from '../../../../../../card/src/contexts/Card';
import { Card } from '../../../../../../card/src/components/Card';
import { CTACellRendererConfig } from '../../types';
import { ActionAuthorizationPayrollButtons } from '../../../../../../authorizations/src/components/Payroll/ActionAuthorizationPayrollButtons';
import { VopStatusWrapper } from '../../VopStatusWrapper';

const defaultCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => rowData[columnDef.field] || '-';

// TODO pass arguments as object
const formatAmountCellRenderer =
  (
    locale?: Nullable<string>,
    isBold?: boolean,
    currencyKey: string = 'currency',
    isNestedProp?: boolean,
    nestedValuesToAccess?: string[],
  ) =>
  (rowData: GenericObject, columnDef: GenericObject) => {
    const areExistingValuesToAccess = Array.isArray(nestedValuesToAccess) && !isEmpty(nestedValuesToAccess);

    if (areExistingValuesToAccess && isNestedProp) {
      const value = rowData[columnDef.field][nestedValuesToAccess[0]];
      const currency = rowData[columnDef.field][currencyKey];
      const decimalPoints = rowData[columnDef.field][nestedValuesToAccess[1]] ?? 2;

      if (isBold) {
        return (
          <Typography component='span' variant='subtitle2'>
            {formatAmount(value, currency, decimalPoints, locale)}
          </Typography>
        );
      }

      return formatAmount(value, currency, decimalPoints, locale);
    }

    if (isBold) {
      return (
        <Typography component='span' variant='subtitle2'>
          {formatAmount(rowData[columnDef.field], rowData[currencyKey], 2, locale)}
        </Typography>
      );
    }

    return formatAmount(rowData[columnDef.field], rowData[currencyKey], 2, locale);
  };

// TODO: expose the locale and timezone in a global window.ECOMMBX_ENV and use the values from there as defaults, if they do not exist use 'en-GB' and timezone of cyprus
// TODO: fix to pass correct locale value in stringToDate instead of undefined
const stringToDateCellRenderer =
  (dateFormatExp: string = 'dd/MM/yyyy', locale?: Nullable<string>, timezone?: string) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
    stringToDate({
      date: rowData[columnDef.field],
      formatExp: dateFormatExp,
      localeName: locale,
      tz: timezone,
    });

const stringToDateTimeCellRenderer =
  (timeFormatExp: string, locale?: Nullable<string>, timezone?: string) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
  (dateFormatExp: string = 'dd/MM/yyyy', timeFormatExp?: string, locale?: Nullable<string>, timezone?: string) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
    !!timeFormatExp ? (
      rowData[columnDef.field] ? (
        <Box display={'flex'}>
          <Box pr={0.5}>
            <Typography component={'span'} variant={'subtitle2'}>
              {stringToDate({
                date: rowData[columnDef.field],
                formatExp: dateFormatExp,
                localeName: locale,
                tz: timezone,
              })}
            </Typography>
          </Box>
          <Typography variant={'body2'} color={'textSecondary'}>
            {stringToDate({
              date: rowData[columnDef.field],
              formatExp: timeFormatExp,
              localeName: locale,
              tz: timezone,
            })}
          </Typography>
        </Box>
      ) : (
        '-'
      )
    ) : (
      stringToDate({
        date: rowData[columnDef.field],
        formatExp: dateFormatExp,
        localeName: locale,
        tz: timezone,
      })
    );

const stringWithPartiallyMaskValue =
  (charsUnmasked: number = 4, totalChars: number = 5) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
    partiallyMaskValue(rowData[columnDef.field].slice(-totalChars), charsUnmasked);

const transactionStatusCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => (
  <TransactionOrderStatusChip statusSerialNumber={rowData[columnDef.field]} />
);

const payrollStatusCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => (
  <PayrollStatusChip statusSerialNumber={parseInt(rowData[columnDef.field])} />
);

const payrollDetailsStatusCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => (
  <PayrollDetailsStatusChip statusSerialNumber={parseInt(rowData[columnDef.field])} />
);

const vopStatusCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => (
  <VopStatusWrapper isTable vopStatus={rowData[columnDef.field]} />
);

const cardStatusCellRenderer = (rowData: GenericObject) => <CardStatusChip cardStatus={rowData.cardStatus} />;

const cardCellRenderer = (config: { size: CardSize; callback: () => void }) => (rowData: GenericObject) =>
  <Card cardData={rowData as CardType} size={config.size} onClick={config.callback} />;

const cardProductCellRenderer =
  (config: { size: CardSize; sizeImg: 'small' | 'medium' | 'big'; callback: () => void }) => (rowData: GenericObject) =>
    (
      <CardCredit
        cardId={''}
        cvv={''}
        size={config.size}
        isPaused={false}
        isVirtual={false}
        isPending={false}
        onClick={() => {}}
        hasSkeleton={false}
        isTerminated={false}
        name={''}
        expirationDate={''}
        number={''}
        descriptionValue={''}
        availableBalanceValue={''}
        backgroundBack={''}
        isCollapsed={false}
        pausedButtonLabel={''}
        pendingButtonLabel={''}
        backgroundCollapsed={''}
        terminatedButtonLabel={''}
        pendingAuthorizationsValue={''}
        backgroundFront={rowData?.assets?.find((img: GenericObject) => img.name === config.sizeImg)?.path ?? ''}
        descriptionLabel={''}
        expirationCardLabel={''}
        cardDetailsButtonLabel={''}
        availableBalanceLabel={''}
        pendingAuthorizationsLabel={''}
        isHightlightedDetails={false}
        isFlipped={false}
        disableHoverState
        ariaControls={'card-credit-placeholder'}
      />
    );

const genericChipCellRenderer =
  (
    statusInfo?: { key: any; value: string; color?: 'default' | 'info' | 'success' | 'error' | 'warning' }[],
    capitalized?: boolean,
  ) =>
  (rowData: GenericObject, columnDef: GenericObject) => {
    const status = statusInfo?.find(status => status.key === rowData[columnDef.field]) ?? {
      key: '',
      value: rowData[columnDef.field],
      color: 'default',
    };
    return <Chip label={status.value} bgColor={status.color ?? 'default'} />;
  };

const transferHistoryTransactionTypeCellRenderer = (orderDirection: string, t: Function) => (
  <Chip
    label={orderDirection === 'O' ? t('Transaction.Transfer.Debit.Type') : t('Transaction.Transfer.Credit.Type')}
    bgColor={orderDirection === 'O' ? 'error' : 'success'}
  />
);

const accountStatmentTransactionTypeCellRenderer = (type: string, t: Function) => (
  <Chip
    label={type === 'DEBIT' ? t('Transaction.Transfer.Debit.Type') : t('Transaction.Transfer.Credit.Type')}
    bgColor={type === 'DEBIT' ? 'error' : 'success'}
  />
);

const staticContentCellRenderer =
  (
    config: (rowData: GenericObject, columnDefs: GenericObject, props?: any) => CTACellRendererConfig[],
    isMobile: boolean,
    configProps?: any,
  ) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
    (
      <Box display={'flex'} alignItems={'center'}>
        {config(rowData, columnDef, configProps).map(({ Component, children, props }) =>
          isMobile && props?.isHiddenInMobile ? null : <Component {...(props || {})} children={children} />,
        )}
      </Box>
    );

const viewDetailsButtonRenderer =
  (callback: Function, fieldName: string, fullWidth?: boolean, title?: string, ariaLabel?: string) =>
  (rowData: GenericObject, columnDef: GenericObject) =>
    (
      <ViewDetailsButton
        identifier={rowData[fieldName]}
        callback={callback}
        fullWidth={fullWidth}
        title={title}
        ariaLabel={ariaLabel}
      />
    );

const textWithIconCellRenderer =
  (icon: string | React.ReactNode, alt?: string) => (rowData: GenericObject, columnDef: GenericObject) => {
    if (typeof icon === 'string') {
      return (
        <Box display='flex' alignItems='center'>
          <img src={icon} alt={alt ? alt : `image for ${rowData[columnDef.field]}`} width={15} height={20} />
          <Box marginLeft={1.5}>{rowData[columnDef.field]}</Box>
        </Box>
      );
    }

    return (
      <Box display='flex' alignItems='center'>
        {icon}
        <Box marginLeft={1.5}>{rowData[columnDef.field]}</Box>
      </Box>
    );
  };

const ctaActionCellRenderer =
  (config: (data: any, cb: Function, t: Function) => CTACellRendererConfig[], callback: Function, t: Function) =>
  (rowData: any, columnDef: GenericObject) =>
    (
      <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
        {config(rowData, callback, t).map(({ Component, icon, items, children, props }, index) => (
          <Component {...(props || {})} key={index} icon={icon} items={items} children={children} />
        ))}
      </Box>
    );

const ctaMobileDrawerActionRenderer = (
  cellActionRenderersConfig: CTACellRendererConfig[],
  toggleMobileDrawer: () => void,
  btnClasses: GenericObject<any>,
) => (
  <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} justifyContent={'center'}>
    {cellActionRenderersConfig.map(({ Component, icon, children, props }, index) => (
      <Component
        classes={{ root: btnClasses.buttonRoot, label: btnClasses.buttonLabel }}
        fullWidth
        {...(props || {})}
        onClick={() => {
          props?.onClick();
          toggleMobileDrawer();
        }}
        key={index}
        icon={icon}>
        {children} {props?.label}
      </Component>
    ))}
  </Box>
);

const actionAuthorizationButtonsRenderer =
  (callback: Function, component?: any) => (rowData: GenericObject, columnDef: GenericObject) =>
    (
      <ActionAuthorizationButtons
        transactionNo={rowData.transactionId as string}
        callback={callback}
        processStatus={rowData.processStatus}>
        {component}
      </ActionAuthorizationButtons>
    );

const actionAuthorizationPayrollButtonsRenderer =
  (callback: Function, component?: any) => (rowData: GenericObject, columnDef: GenericObject) =>
    (
      <ActionAuthorizationPayrollButtons
        payrollFileId={rowData.payrollFileId as string}
        callback={callback}
        status={rowData.status}>
        {component}
      </ActionAuthorizationPayrollButtons>
    );

const payrollRecordStatusCellRenderer = (rowData: GenericObject, columnDef: GenericObject) => (
  <PayrollRecordStatusChip recordStatusDescription={rowData[columnDef.field]} />
);

export {
  defaultCellRenderer,
  textWithIconCellRenderer,
  formatAmountCellRenderer,
  stringToDateCellRenderer,
  stringWithPartiallyMaskValue,
  genericChipCellRenderer,
  transactionStatusCellRenderer,
  cardStatusCellRenderer,
  cardCellRenderer,
  viewDetailsButtonRenderer,
  ctaActionCellRenderer,
  staticContentCellRenderer,
  stringToDateTimeCellRenderer,
  actionAuthorizationButtonsRenderer,
  actionAuthorizationPayrollButtonsRenderer,
  payrollRecordStatusCellRenderer,
  payrollStatusCellRenderer,
  payrollDetailsStatusCellRenderer,
  ctaMobileDrawerActionRenderer,
  transferHistoryTransactionTypeCellRenderer,
  accountStatmentTransactionTypeCellRenderer,
  cardProductCellRenderer,
  vopStatusCellRenderer,
};
